import { HOME_COPY } from "@/components/dashboard/home-copy";
/** My sessions: one authorized List, with day groups and a read state per course. */
import { useQueries, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { Bento } from "@/components/ui/box";
import { MyGoogleCalendars } from "@/components/courses/MyGoogleCalendars";
import type { Box, ListBox, Read } from "@/components/ui/box.schema";
import { Button } from "@/components/ui/button";
import { sessionIndexRowAct, sessionIndexRowFacts, sessionIndexRowTitle } from "@/components/session/SessionIndexRow";
import { FrameHeader } from "@/components/workspace/FrameHeader";
import { useAuth } from "@/contexts/AuthContext";
import { attendanceApi, type Session } from "@/lib/api/attendance";
import { coursesApi, type Course } from "@/lib/api/courses";
import { classTitle } from "@/lib/class-name";
import { groupSessionIndexByDay, sessionIndexDate, sessionIndexTime, type SessionIndexRecord } from "@/lib/session-index";
import { courseWorkspacePath } from "@/navigation/routes";

export type SessionListWorkspace = "instructor" | "adminTeaching" | "student";
const COPY = {
  instructor: { eyebrow: "Teaching", meta: "Every session in the courses you teach, by day", home: "/instructor" },
  adminTeaching: { eyebrow: "Teaching", meta: "Every session in the courses you teach, by day", home: "/admin/teaching" },
  student: { eyebrow: "Learning", meta: "Every session in the courses you take, by day", home: "/student" },
};
type IndexRow = { key: string; group: string; record: SessionIndexRecord & { session: Session } } | { key: string; group: string; course: Course; state: "failed" | "loading"; retry: () => void };
interface Index { rows: IndexRow[]; known: number; hidden: number; complete: boolean }

export function SessionListPage({ workspace }: { workspace: SessionListWorkspace }) {
  const { me } = useAuth();
  const copy = COPY[workspace];
  const [search, setSearch] = useState("");
  const [now] = useState(() => new Date());
  const courses = useQuery({
    queryKey: ["session-index-courses", me?.tenant_id, me?.user_id, workspace],
    queryFn: () => coursesApi.list(workspace === "student" ? { learning: true } : {}),
    enabled: Boolean(me?.tenant_id && me?.user_id), retry: false,
  });
  // A failed authorization withdraws cached course links and their schedules.
  const scoped = (courses.isError ? [] : courses.data ?? []).filter(course => course.status !== "archived" && (workspace === "student" || course.can_teach === true));
  const schedules = useQueries({ queries: scoped.map(course => ({ queryKey: ["course-workspace-sessions", me?.tenant_id, me?.user_id, course.id], queryFn: () => attendanceApi.listSessions(course.id), retry: false })) });
  const incomplete: IndexRow[] = [];
  const records = scoped.flatMap((course, index) => {
    const query = schedules[index];
    const wrong = query.data?.some(session => session.course_id !== course.id);
    if (query.isError || query.isPending || wrong) {
      incomplete.push({ key: `course:${course.id}`, group: HOME_COPY.otherSchedules, course, state: query.isPending ? "loading" : "failed", retry: () => void query.refetch() });
      return [];
    }
    return (query.data ?? []).map(session => ({ course, session }));
  });
  const needle = search.trim().toLocaleLowerCase();
  const matching = records.filter(row => [row.course.code, row.course.title, classTitle(row.session, row.course.timezone ?? "UTC", { underDay: true }), sessionIndexDate(row), sessionIndexTime(row), row.session.session_date].join(" ").toLocaleLowerCase().includes(needle));
  const grouped: IndexRow[] = groupSessionIndexByDay(matching, now).flatMap(day => day.rows.map(record => ({ key: `session:${record.session.id}`, group: day.label, record: record as SessionIndexRecord & { session: Session } })));
  const value: Index = { rows: [...grouped, ...incomplete], known: records.length, hidden: records.length - matching.length, complete: incomplete.length === 0 };
  const retry = () => { if (courses.isError) void courses.refetch(); for (const row of incomplete) if (!("record" in row) && row.state === "failed") row.retry(); };
  const read: Read<Index> = !me?.tenant_id || !me.user_id || courses.isPending ? { status: "loading" }
    : courses.isError ? { status: "failed", sentence: HOME_COPY.sessionsFailed, retry }
      : value.rows.length ? { status: "ready", value, readAt: new Date(courses.dataUpdatedAt).toISOString() }
        : { status: "empty", sentence: needle ? HOME_COPY.noMatch : HOME_COPY.noSessions };
  const box: ListBox<Index, IndexRow> = {
    kind: "list", id: "my-sessions", label: HOME_COPY.sessions, job: "Find authorized sessions by day and navigate to their exact course context", owner: { system: "imam" }, fold: "never", detail: () => "", read,
    find: { placeholder: HOME_COPY.findSession, onQueryChange: setSearch, hidden: value.hidden },
    ...(incomplete.some(row => !("record" in row) && row.state === "failed") ? { act: { does: "check" as const, verb: HOME_COPY.tryAgain, run: retry } } : {}),
    state: current => ({ class: "quiet", word: current.complete ? HOME_COPY.sessionCount(current.known) : HOME_COPY.incompleteSessions(current.known) }), rows: current => current.rows,
    row: { key: row => row.key, group: row => row.group,
      title: row => "record" in row ? sessionIndexRowTitle(row.record) : HOME_COPY.scheduleRead(row.course.code, row.state === "loading"),
      nav: row => "record" in row ? `${courseWorkspacePath(workspace, row.record.course.id)}?session=${encodeURIComponent(row.record.session.id)}` : undefined,
      facts: row => "record" in row ? sessionIndexRowFacts(row.record) : "",
      act: row => "record" in row ? sessionIndexRowAct(row.record) : undefined,
    },
  };
  return <div className="app-page" data-session-index>
    <FrameHeader eyebrow={copy.eyebrow} title={HOME_COPY.mySessions} meta={copy.meta} quietAction={<Button asChild variant="outline"><Link to={copy.home}>Return to Home</Link></Button>} />
    <Bento key={`${me?.tenant_id}:${me?.user_id}:${workspace}`} id="session-index" journey="my-sessions" boxes={[box] as Box[]} />
    <MyGoogleCalendars placement="sessions" />
  </div>;
}
